import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import API from "@src/API.js";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import { toast } from "react-toastify";
import { refreshPersonSkillsData } from "../../query/usePersonSkills";

const LEVEL_NAMES = {
  1: "Beginner",
  2: "Junior",
  3: "Intermediate",
  4: "Senior",
  5: "Guru",
};

export default function SkillTestRunner() {
  const { id: idStr } = useParams();
  const testId = Number.parseInt(idStr, 10);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [payload, setPayload] = useState(null);
  const [selectedAnswerId, setSelectedAnswerId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const screeningToken = useMemo(() => {
    const fromQuery = searchParams.get("token");
    if (fromQuery) return fromQuery;
    return window.sessionStorage.getItem("ScreeningToken") || null;
  }, [searchParams]);

  const tokenHeaders = useMemo(() => {
    if (!screeningToken) return {};
    return { "X-Screening-Token": screeningToken };
  }, [screeningToken]);

  const load = useCallback(async () => {
    const resp = await API.getAsync(`/Skill/Test/${testId}`, null, { headers: tokenHeaders });
    if (resp?.ok) {
      setPayload(resp.jsonData);
      setSelectedAnswerId(null);
    } else {
      toast.error(resp?.jsonData?.message || "Could not load test");
    }
  }, [testId, tokenHeaders]);

  useEffect(() => {
    if (testId) load();
  }, [testId, load]);

  async function submitAnswer(dontKnow) {
    if (busy || !payload?.question) return;
    setBusy(true);
    try {
      const resp = await API.postAsync(
        `/Skill/Test/${testId}/Answer`,
        {
          skillQuestionID: payload.question.id,
          selectedAnswerID: dontKnow ? null : selectedAnswerId,
          dontKnow: !!dontKnow,
        },
        { headers: tokenHeaders }
      );
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Answer failed");
        return;
      }
      const next = resp.jsonData;
      setPayload(next);
      setSelectedAnswerId(null);
      if (next?.done || next?.status === "Completed") {
        await complete();
      }
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    const resp = await API.postAsync(`/Skill/Test/${testId}/Complete`, {}, { headers: tokenHeaders });
    if (resp?.ok) {
      setResult(resp.jsonData);
      window.sessionStorage.removeItem("ScreeningToken");
      try {
        await refreshPersonSkillsData(queryClient);
      } catch {
        /* token-only candidates may have no session */
      }
    } else {
      toast.error(resp?.jsonData?.message || "Complete failed");
    }
  }

  if (result) {
    return (
      <div className="pagebody">
        <BreadCrumb
          props={[
            { route: "/skills", label: "Skills" },
            { route: "/skillTests", label: "Skill Tests" },
            { label: "Results" },
          ]}
        />
        <div className="tabbody">
          <h1>Test complete</h1>
          <p>Score: {result.score}</p>
          <p>
            Level: {LEVEL_NAMES[result.skillLevel] || result.skillLevel}
            {result.certified
              ? ` — Verified badge applied (${LEVEL_NAMES[result.verifiedSkillLevel] || result.verifiedSkillLevel})`
              : " — Discovery only (no verified badge)"}
          </p>
          {screeningToken ? (
            <p>You can close this window. The recruiter can see your results on the job match page.</p>
          ) : (
            <CustomButton type="button" variant="outlined" color="secondary" onClick={() => navigate("/skills")}>
              Back to Skills
            </CustomButton>
          )}
        </div>
      </div>
    );
  }

  if (!payload) return <p className="pagebody">Loading test…</p>;

  if (payload.done) {
    return (
      <div className="pagebody">
        <div className="tabbody">
          <p>All questions answered.</p>
          <CustomButton type="button" variant="contained" color="primary" disabled={busy} onClick={() => complete()}>
            Finish
          </CustomButton>
        </div>
      </div>
    );
  }

  const q = payload.question;
  return (
    <div className="pagebody">
      <BreadCrumb
        props={[
          { route: "/skills", label: "Skills" },
          { route: "/skillTests", label: "Skill Tests" },
          { label: `Question ${(payload.sequence ?? 0) + 1}` },
        ]}
      />
      <div className="tabbody">
        <h1>Skill test</h1>
        <p className="byline">
          Mode {payload.mode === 2 ? "Type 2 (discover)" : "Type 1 (certify)"} · Band{" "}
          {LEVEL_NAMES[payload.skillLevel] || payload.skillLevel}
        </p>
        <p className="questionText">{q.questionText}</p>
        <ul className="answerList">
          {(q.answers || []).map((a) => (
            <li key={a.id}>
              <label>
                <input
                  type="radio"
                  name="answer"
                  checked={selectedAnswerId === a.id}
                  onChange={() => setSelectedAnswerId(a.id)}
                />{" "}
                {a.text}
              </label>
            </li>
          ))}
        </ul>
        <div className="formActions">
          <CustomButton
            type="button"
            variant="contained"
            color="primary"
            disabled={busy || !selectedAnswerId}
            onClick={() => submitAnswer(false)}
          >
            Next
          </CustomButton>
          <CustomButton type="button" variant="outlined" color="secondary" disabled={busy} onClick={() => submitAnswer(true)}>
            Don&apos;t know
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
